(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const ease = value => value * value * (3 - 2 * value);
  const scrollTargets = () => [window, document.querySelector('.page-wrapper')].filter((item, index, list) => item && list.indexOf(item) === index);

  function setChromeHeight() {
    const group = document.querySelector('#header-group');
    if (!group) return;
    document.documentElement.style.setProperty('--wms-initial-header-height', `${Math.round(group.getBoundingClientRect().height)}px`);
  }

  function initStory(root = document) {
    root.querySelectorAll?.('[data-wms-story]:not([data-wms-ready])').forEach(story => {
      story.dataset.wmsReady = 'true';
      const images = [...story.querySelectorAll('[data-wms-image]')];
      const sceneData = story.querySelector('[data-wms-scenes]');
      let scenes = [];
      try { scenes = JSON.parse(sceneData?.textContent || '[]'); } catch (error) { scenes = []; }
      const kicker = story.querySelector('[data-wms-kicker]');
      const title = story.querySelector('[data-wms-title]');
      const body = story.querySelector('[data-wms-body]');
      const copy = story.querySelector('[data-wms-copy]');
      const progress = story.querySelector('[data-wms-progress]');
      const duration = Math.max(4000, Number(story.dataset.wmsSceneDuration) || 6000);
      const escapeHTML = value => String(value || '').replace(/[&<>"']/g, character => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[character]));
      let active = 0;
      let timer = null;
      let progressAnimation = null;
      let visible = true;
      let pageReady = document.readyState === 'complete';

      const hydrateImage = image => {
        if (!image || !image.dataset.wmsSrc) return Promise.resolve();
        if (image.dataset.wmsSrcset) image.srcset = image.dataset.wmsSrcset;
        image.src = image.dataset.wmsSrc;
        delete image.dataset.wmsSrc;
        delete image.dataset.wmsSrcset;
        return image.decode?.().catch(() => {}) || Promise.resolve();
      };

      const hydrateNextScene = () => {
        if (images.length <= 1) return;
        const next = (active + 1) % images.length;
        hydrateImage(images[next]);
      };

      const setStoryHeight = () => {
        const viewport = window.visualViewport?.height || innerHeight;
        const chromeHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--wms-initial-header-height')) || 0;
        const minimum = innerWidth <= 420 ? 400 : innerWidth <= 1399 ? 450 : 620;
        const stageHeight = Math.max(minimum, viewport - chromeHeight + 1);
        story.style.setProperty('--wms-stage-height', `${Math.ceil(stageHeight)}px`);
      };

      const writeScene = (index, initial = false) => {
        const scene = scenes[index];
        if (!scene) return;
        hydrateImage(images[index]);
        story.dataset.wmsScene = String(index);
        images.forEach((image, imageIndex) => {
          image.classList.toggle('is-active', imageIndex === index);
          image.classList.toggle('is-previous', imageIndex === (index - 1 + images.length) % images.length);
        });

        const applyCopy = () => {
          if (kicker) kicker.textContent = scene.kicker || '';
          if (title) {
            const lead = String(scene.accentLead || '').trim();
            const accent = String(scene.accent || '').trim();
            const accentLine = lead || accent
              ? `<br><span class="wms-story__accent-line">${lead ? `<span>${escapeHTML(lead)}</span>` : ''}${lead && accent ? ' ' : ''}${accent ? `<em>${escapeHTML(accent)}</em>` : ''}</span>`
              : '';
            title.innerHTML = `${escapeHTML(scene.title || '')}${accentLine}`;
          }
          if (body) body.textContent = scene.body || '';
        };

        if (initial || reduced.matches || !copy?.animate) {
          applyCopy();
          return;
        }

        copy.getAnimations().forEach(animation => animation.cancel());
        const out = copy.animate(
          [{opacity:1},{opacity:0}],
          {duration:280,easing:'cubic-bezier(.22,.61,.36,1)',fill:'forwards'}
        );
        out.finished.catch(() => {}).then(() => {
          applyCopy();
          copy.getAnimations().forEach(animation => animation.cancel());
          copy.animate(
            [{opacity:0},{opacity:1}],
            {duration:480,easing:'cubic-bezier(.22,.61,.36,1)',fill:'both'}
          );
        });
      };

      const animateProgress = () => {
        progressAnimation?.cancel?.();
        if (!progress) return;
        progress.style.transform = 'scaleX(0)';
        if (reduced.matches || !visible || scenes.length <= 1) {
          progress.style.transform = `scaleX(${scenes.length <= 1 ? 1 : 0})`;
          return;
        }
        progressAnimation = progress.animate(
          [{transform:'scaleX(0)'},{transform:'scaleX(1)'}],
          {duration,easing:'linear',fill:'forwards'}
        );
      };

      const stop = () => {
        clearTimeout(timer);
        timer = null;
        progressAnimation?.cancel?.();
        progressAnimation = null;
      };

      const schedule = () => {
        stop();
        if (reduced.matches || !visible || document.hidden || scenes.length <= 1) return;
        animateProgress();
        timer = setTimeout(() => {
          active = (active + 1) % scenes.length;
          writeScene(active);
          hydrateNextScene();
          schedule();
        }, duration);
      };

      const start = () => {
        if (!pageReady || !visible || document.hidden) return;
        schedule();
      };

      const enableAutoplay = () => {
        pageReady = true;
        story.classList.add('is-motion-ready');
        hydrateNextScene();
        start();
      };

      const observer = new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting && entry.intersectionRatio > .18;
        if (visible) start(); else stop();
      }, {threshold:[0,.18,.45]});
      observer.observe(story);

      document.addEventListener('visibilitychange', () => document.hidden ? stop() : start());
      addEventListener('resize', setStoryHeight, {passive:true});
      window.visualViewport?.addEventListener?.('resize', setStoryHeight, {passive:true});
      reduced.addEventListener?.('change', () => {
        if (reduced.matches) stop(); else start();
      });

      setStoryHeight();
      writeScene(0, true);
      story._wmsRender = setStoryHeight;
      if (pageReady) enableAutoplay();
      else addEventListener('load', enableAutoplay, {once:true});
    });
  }

  function initReveal(root = document) {
    const items = [...(root.querySelectorAll?.('[data-wms-reveal]:not([data-wms-reveal-ready])') || [])];
    if (!items.length) return;
    if (reduced.matches) return items.forEach(item => item.classList.add('is-visible', 'has-entered'));
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      const item = entry.target;
      const rect = entry.boundingClientRect;
      if (entry.isIntersecting) {
        item.classList.remove('is-above', 'is-below');
        requestAnimationFrame(() => item.classList.add('is-visible', 'has-entered'));
      } else {
        item.classList.remove('is-visible');
        item.classList.toggle('is-above', rect.bottom <= innerHeight * .18);
        item.classList.toggle('is-below', rect.top >= innerHeight * .82);
      }
    }), {threshold:[0,.08,.22],rootMargin:'-5% 0px -7%'});
    items.forEach(item => { item.dataset.wmsRevealReady = 'true'; observer.observe(item); });
  }

  function initTaste(root = document) {
    root.querySelectorAll?.('[data-wms-taste]:not([data-wms-taste-ready])').forEach(section => {
      section.dataset.wmsTasteReady = 'true';
      const cards = [...section.querySelectorAll('[data-wms-taste-card]')];
      const progress = section.querySelector('[data-wms-taste-progress]');
      const rail = section.querySelector('.wms-taste__grid');
      let active = 0;
      let timer;
      let paused = false;
      const activate = (index, shouldScroll = false) => {
        active = (index + cards.length) % cards.length;
        cards.forEach((card, cardIndex) => card.classList.toggle('is-active', cardIndex === active));
        if (progress) progress.style.transform = `scaleX(${(active + 1) / cards.length})`;
        if (shouldScroll && rail && matchMedia('(max-width:749px)').matches) {
          const card = cards[active];
          if (card) {
            const railRect = rail.getBoundingClientRect();
            const cardRect = card.getBoundingClientRect();
            const left = rail.scrollLeft + cardRect.left - railRect.left - (rail.clientWidth - cardRect.width) / 2;
            rail.scrollTo({ left, behavior: 'smooth' });
          }
        }
      };
      const stop = () => clearInterval(timer);
      const start = () => { stop(); if (!reduced.matches && !paused && cards.length > 1) timer = setInterval(() => activate(active + 1, true), 4800); };
      cards.forEach((card, index) => {
        card.addEventListener('pointerenter', () => { paused = true; stop(); activate(index); });
        card.addEventListener('pointerleave', () => { paused = false; start(); });
        card.addEventListener('focusin', () => { paused = true; stop(); activate(index); });
        card.addEventListener('focusout', () => { paused = false; start(); });
      });
      if (rail) {
        let scrollFrame = 0;
        rail.addEventListener('scroll', () => {
          if (!matchMedia('(max-width:749px)').matches || scrollFrame) return;
          scrollFrame = requestAnimationFrame(() => {
            scrollFrame = 0;
            const center = rail.getBoundingClientRect().left + rail.clientWidth / 2;
            let nearest = 0;
            let distance = Infinity;
            cards.forEach((card, index) => {
              const rect = card.getBoundingClientRect();
              const next = Math.abs(rect.left + rect.width / 2 - center);
              if (next < distance) { distance = next; nearest = index; }
            });
            if (nearest !== active) activate(nearest);
          });
        }, { passive: true });
      }
      new IntersectionObserver(([entry]) => entry.isIntersecting ? start() : stop(), {threshold:.24}).observe(section);
      activate(0);
    });
  }

  function initDynamicMega(root = document) {
    root.querySelectorAll?.('[data-wms-dynamic-mega]:not([data-wms-mega-ready])').forEach(grid => {
      const triggers = [...grid.querySelectorAll('[data-wms-mega-product-trigger]')];
      const panels = [...grid.querySelectorAll('[data-wms-mega-product-panel]')];
      if (!triggers.length || !panels.length) return;
      grid.dataset.wmsMegaReady = 'true';
      let activeKey = panels[0]?.dataset.wmsMegaProductPanel || '';

      const activate = key => {
        if (!panels.some(panel => panel.dataset.wmsMegaProductPanel === key)) return;
        if (key === activeKey && panels.some(panel => panel.dataset.wmsMegaProductPanel === key && panel.classList.contains('is-active'))) return;
        activeKey = key;
        triggers.forEach(trigger => {
          const active = trigger.dataset.wmsMegaProductTrigger === key;
          trigger.classList.toggle('is-active', active);
          trigger.setAttribute('aria-current', active ? 'true' : 'false');
        });
        panels.forEach(panel => {
          const active = panel.dataset.wmsMegaProductPanel === key;
          panel.classList.toggle('is-active', active);
          panel.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
      };

      triggers.forEach(trigger => {
        trigger.addEventListener('pointerenter', () => activate(trigger.dataset.wmsMegaProductTrigger));
        trigger.addEventListener('focusin', () => activate(trigger.dataset.wmsMegaProductTrigger));
      });
      activate(activeKey);
    });
  }

  function initBrewDialogs(root = document) {
    root.querySelectorAll?.('[data-wms-brew-experience]:not([data-wms-brew-ready])').forEach(section => {
      section.dataset.wmsBrewReady = 'true';

      const selectionView = section.querySelector('[data-wms-brew-selection-view]');
      const recipeView = section.querySelector('[data-wms-brew-recipe-view]');
      const recipeMain = section.querySelector('[data-wms-brew-recipe-main]');
      const recipeActions = section.querySelector('[data-wms-brew-recipe-actions]');
      const defaultMedia = section.querySelector('[data-wms-brew-default-media]');
      const productMedia = section.querySelector('[data-wms-brew-product-media]');
      const productImage = section.querySelector('[data-wms-brew-product-image]');
      const mediaMethod = section.querySelector('[data-wms-brew-media-method]');
      const mediaProduct = section.querySelector('[data-wms-brew-media-product]');
      const backButton = section.querySelector('[data-wms-brew-back]');
      const productsBox = section.querySelector('[data-wms-brew-products]');
      const recommendKicker = section.querySelector('[data-wms-brew-recommend-kicker]');
      const recommendTitle = section.querySelector('[data-wms-brew-recommend-title]');
      const methodButtons = [...section.querySelectorAll('[data-wms-brew-method]')];
      const recipeKicker = section.querySelector('[data-wms-brew-recipe-kicker]');
      const recipeProduct = section.querySelector('[data-wms-brew-recipe-product]');
      const recipeMethod = section.querySelector('[data-wms-brew-recipe-method]');
      const recipeLead = section.querySelector('[data-wms-brew-recipe-lead]');
      const recipeMetrics = section.querySelector('[data-wms-brew-recipe-metrics]');
      const recipeSteps = section.querySelector('[data-wms-brew-recipe-steps]');
      const fineTune = section.querySelector('[data-wms-brew-fine-tune]');
      const detailProductLink = section.querySelector('[data-wms-brew-detail-product-link]');

      let catalog = [];
      try { catalog = JSON.parse(section.querySelector('[data-wms-brew-catalog]')?.textContent || '[]'); } catch (_) {}
      let selectedMethodKey = null;
      let lastRecipeButton = null;
      const recipeImageCache = new Map();
      let recipesPromise = null;

      const ensureRecipes = () => {
        if (window.WemondProductRecipes && window.WemondRecipeUtils) return Promise.resolve();
        if (recipesPromise) return recipesPromise;
        const url = section.dataset.wmsRecipesUrl;
        if (!url) return Promise.resolve();

        recipesPromise = new Promise((resolve, reject) => {
          const existing = [...document.scripts].find((script) => script.src === url);
          const script = existing || document.createElement('script');
          const ready = () => {
            if (window.WemondProductRecipes && window.WemondRecipeUtils) resolve();
            else reject(new Error('Demleme reçeteleri hazırlanamadı.'));
          };
          script.addEventListener('load', ready, { once: true });
          script.addEventListener('error', () => reject(new Error('Demleme reçeteleri yüklenemedi.')), { once: true });
          if (!existing) {
            script.src = url;
            script.dataset.wmsRecipesAsset = 'true';
            document.head.append(script);
          } else if (window.WemondProductRecipes && window.WemondRecipeUtils) {
            ready();
          }
        }).catch((error) => {
          recipesPromise = null;
          throw error;
        });
        return recipesPromise;
      };

      const methods = {
        'espresso': {
          label:'Espresso',
          priority:['signature espresso','house espresso','daily espresso','espresso blend','cerrado','honduras','antigua','huila'],
          secondary:['brazil','rio minas','sumatra','guatemala','colombia'],
          exclude:['anaerobic','yirgacheffe'],
          metrics:[['Kahve','18 g'],['Çıktı','36 g'],['Oran','1:2'],['Sıcaklık','93°C'],['Öğütüm','İnce'],['Süre','27–31 sn']],
          steps:['Portafiltreyi kuru ve sıcak tut; 18 g kahveyi eşit dağıt ve düz tamp yap.','İlk damlaların dengeli geldiğini kontrol ederek 36 g çıktıyı hedefle.','Toplam akışı 27–31 saniye aralığında tut; süreyi öncelikle öğütümle düzelt.','Shotı karıştırıp tek başına tadına bak; ardından gerekirse oranı 1:1.9–1:2.2 arasında küçük adımlarla değiştir.']
        },
        'v60': {
          label:'V60',
          priority:['yirgacheffe','el jardin','finca el jardín','huila','signature filter','antigua','sidamo','peru'],
          secondary:['guatemala','colombia','rwanda','nyamasheke'],
          exclude:['espresso blend','rio minas','sumatra'],
          metrics:[['Kahve','15 g'],['Su','240 g'],['Oran','1:16'],['Sıcaklık','92°C'],['Öğütüm','Orta-ince'],['Süre','2:40–3:00']],
          steps:['Filtreyi yıka, ekipmanı ısıt ve 15 g kahveyi düz bir yatak oluşturacak şekilde yerleştir.','45 g suyla bütün yatağı ıslat; 40 saniye blooming yap.','120 g, 180 g ve 240 g olmak üzere sakin ve kontrollü üç ana döküş yap.','Son döküşten sonra yatağı hafifçe düzle; toplam akışı yaklaşık 2:40–3:00 arasında bitir.']
        },
        'chemex': {
          label:'Chemex',
          priority:['yirgacheffe','el jardin','finca el jardín','huila','antigua','peru','sidamo','signature filter'],
          secondary:['guatemala','colombia','rwanda'],
          exclude:['espresso blend','rio minas','sumatra'],
          metrics:[['Kahve','30 g'],['Su','500 g'],['Oran','1:16,7'],['Sıcaklık','93°C'],['Öğütüm','Orta'],['Süre','4:00–4:30']],
          steps:['Kalın Chemex filtresini bol sıcak suyla yıka ve serverı tamamen ısıt.','30 g kahveyi ekle; 75 g suyla 45 saniye blooming yap.','200 g, 350 g ve 500 g seviyelerine sakin döküşlerle ilerle; yatağın kenarını gereksiz yere yıkama.','Akış 4:30’u belirgin geçerse öğütümü kalınlaştır; çok hızlıysa küçük bir adım incelt.']
        },
        'aeropress': {
          label:'AeroPress',
          priority:['antigua','el jardin','finca el jardín','huila','honduras','cerrado','peru','yirgacheffe'],
          secondary:['guatemala','colombia','filter','brazil'],
          exclude:[],
          metrics:[['Kahve','16 g'],['Su','230 g'],['Oran','1:14,4'],['Sıcaklık','90°C'],['Öğütüm','Orta-ince'],['Süre','1:45–2:00']],
          steps:['16 g kahveyi hazneye ekle ve yatağı düzle.','50 g suyla başla; 20 saniye boyunca nazikçe karıştır.','230 g’a tamamla ve 1:15’e kadar bekle.','Kapağı tak; 30–40 saniyede sabit ve yavaş basınçla presle.']
        },
        'filter-machine': {
          label:'Filtre Kahve Makinesi',
          priority:['daily filter','house filter','honduras','cerrado','huila','antigua'],
          secondary:['brazil','guatemala','peru','colombia','signature filter'],
          exclude:['anaerobic'],
          metrics:[['Kahve','60 g'],['Su','1.000 g'],['Oran','1:16,7'],['Sıcaklık','92°C'],['Öğütüm','Orta'],['Süre','5–6 dk']],
          steps:['Kağıt filtreyi yıka ve makine haznesini temiz suyla hazırla.','1 litre su için 60 g orta öğütüm kahveyi yatağa eşit dağıt.','İlk 45 saniyede bütün yatağın eşit ıslandığını kontrol et.','Demleme uzuyorsa biraz kalınlaştır; zayıf ve kısa kalıyorsa öğütümü küçük adımlarla incelt.']
        },
        'cold-brew': {
          label:'Cold Brew',
          priority:['cerrado','honduras','rio minas','sumatra','daily espresso','brazil','peru'],
          secondary:['house espresso','antigua','guatemala'],
          exclude:['yirgacheffe','anaerobic'],
          metrics:[['Kahve','70 g'],['Su','1.000 g'],['Oran','1:14'],['Sıcaklık','Soğuk'],['Öğütüm','Kalın'],['Süre','10–12 saat']],
          steps:['70 g kalın öğütülmüş kahveyi temiz bir demleme kabına al.','1 litre soğuk suyu ekle; bütün kahvenin ıslandığından emin olacak kadar nazikçe karıştır.','Buzdolabında 10–12 saat temas ettir; kabı bu sürede çalkalama.','Önce kaba, ardından kağıt veya ince filtreden süz ve servis öncesi tamamen soğut.']
        },
        'japanese': {
          label:'Japanese Iced',
          priority:['yirgacheffe','el jardin','finca el jardín','nyamasheke','rwanda','la bonita','sidamo','huila','antigua'],
          secondary:['anaerobic','signature filter','colombia','guatemala'],
          exclude:['rio minas','sumatra'],
          metrics:[['Kahve','20 g'],['Sıcak su','180 g'],['Buz','120 g'],['Sıcaklık','92°C'],['Öğütüm','Orta-ince'],['Süre','2:30–3:00']],
          steps:['Server’a 120 g iri buz koy; dripper ve filtreyi ayrı sıcak suyla ısıt.','20 g kahveyi 50 g suyla tamamen ıslat ve 40 saniye bloom et.','1:10’da 120 g’a, 1:45 civarında 180 g sıcak suya tamamla.','Demleme bittiğinde serverı çevirerek kalan buzu erit; aromayı kaybetmeden hemen servis et.']
        },
        'french-press': {
          label:'French Press',
          priority:['cerrado','honduras','sumatra','rio minas','antigua','peru','huila'],
          secondary:['brazil','guatemala','colombia'],
          exclude:['yirgacheffe','anaerobic'],
          metrics:[['Kahve','30 g'],['Su','500 g'],['Oran','1:16,7'],['Sıcaklık','94°C'],['Öğütüm','Kalın'],['Süre','4:30']],
          steps:['30 g kalın öğütülmüş kahveyi French Press’e ekle.','500 g suyu tek seferde dök ve bütün yatağı tamamen ıslat.','4 dakika bekle; yüzeydeki kabuğu kır ve 30 saniye daha dinlendir.','Pistonu yavaşça indir; kahveyi bekletmeden servis kabına aktar.']
        }
      };

      const normalize = value => String(value || '').toLocaleLowerCase('tr-TR');
      const productText = product => normalize([
        product.title,
        product.handle,
        product.product_type,
        product?.coffee_meta?.country,
        product?.coffee_meta?.region,
        product?.coffee_meta?.process,
        product?.coffee_meta?.tasting_notes,
        product?.coffee_meta?.recommended_brew,
        ...(Array.isArray(product.tags) ? product.tags : []),
        ...(Array.isArray(product.collections) ? product.collections : [])
      ].join(' '));
      const money = cents => typeof cents === 'number'
        ? new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(cents / 100)
        : '';
      const escapeHTML = value => String(value || '').replace(/[&<>'"]/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[ch]));
      const coffeeWords = ['250g','kahve','coffee','espresso','filter','cerrado','honduras','huila','yirgacheffe','antigua','sidamo','peru','sumatra','rwanda','nyamasheke','rio minas'];

      function looksLikeCoffee(product) {
        const text = productText(product);
        return coffeeWords.some(word => text.includes(normalize(word)));
      }

      function score(product, method) {
        const text = productText(product);
        if (!looksLikeCoffee(product) || product.available === false) return -9999;
        if (method.exclude.some(word => text.includes(normalize(word)))) return -1500;
        let points = 0;
        method.priority.forEach((word, index) => {
          if (text.includes(normalize(word))) points = Math.max(points, 1000 - index * 85);
        });
        method.secondary.forEach((word, index) => {
          if (text.includes(normalize(word))) points = Math.max(points, 420 - index * 38);
        });
        const recommended = normalize(product?.coffee_meta?.recommended_brew);
        if (recommended && (recommended.includes(normalize(method.label)) || recommended.includes(normalize(selectedMethodKey)))) points += 240;
        if (/\b250\s*g\b|250g/.test(text)) points += 8;
        return points;
      }

      function selectProducts(method) {
        // The shared recipe file is the single source for which coffee has a
        // recipe for which method (same as product pages and the guides page).
        // Only coffees with a real recipe for this method are recommended; the
        // keyword score just orders them. Without the file, keep the old ranking.
        const utils = window.WemondRecipeUtils;
        const hasRecipes = Boolean(window.WemondProductRecipes && utils?.find);
        const ranked = catalog
          .map((product, index) => ({product, index, points:score(product, method)}))
          .filter(item => item.product?.title && item.product?.url && (hasRecipes
            ? item.points > -1000 && utils.find(item.product.handle, selectedMethodKey)
            : item.points > 0))
          .sort((a,b) => b.points - a.points || a.index - b.index)
          .map(item => item.product);
        return ranked.slice(0,2);
      }

      function productProfile(product) {
        const text = productText(product);
        if (text.includes('el jardin') || text.includes('el-jardin') || text.includes('finca el jardín')) return {
          family:'el-jardin',
          lead:'Lemon tart canlılığını, bektaşi üzümünün parlak asiditesini ve vanilya tatlılığını 120 saatlik özel işlemenin önüne geçirmeden temiz tutacak bir başlangıç reçetesi.',
          tune:'Fermentasyon karakteri ağırlaşırsa sıcaklığı 1–2°C düşür veya teması kısalt. Bektaşi üzümü fazla keskinse öğütümü çok az kalınlaştır; vanilya tatlılığı gerideyse önce küçük bir adım incelt.'
        };
        if (text.includes('yirgacheffe') || text.includes('sidamo')) return {
          family:'floral',
          lead:'Floral, narenciye ve çay benzeri detayları kapatmadan; aromayı açık ve bitişi temiz tutacak bir başlangıç reçetesi.',
          tune:'Fincan keskinleşirse 1–2°C düşür veya bir kademe kalınlaştır. Tatlılık geride kalıyorsa sıcaklığı değil önce öğütümü küçük bir adım incelt.'
        };
        if (text.includes('nyamasheke') || text.includes('rwanda') || text.includes('la bonita') || text.includes('anaerobic')) return {
          family:'anaerobic',
          lead:'Fermentasyon karakterini parfümsü veya ağır hale getirmeden; meyveyi parlak, tatlılığı kontrollü gösterecek şekilde ayarlandı.',
          tune:'Fermente notalar baskınlaşırsa suyu 1–2°C düşür ve teması kısalt. Meyve sönükse öğütümü çok az incelt; sıcaklığı bir anda yükseltme.'
        };
        if (text.includes('huila') || text.includes('colombia')) return {
          family:'balanced',
          lead:'Karamel tatlılığını ve temiz meyve asiditesini birlikte gösterecek, gövdeyi gereksiz yere ağırlaştırmayacak dengeli başlangıç reçetesi.',
          tune:'Asidite sivri kalırsa teması biraz uzat; fincan kuru ve ağırsa bir kademe kalınlaştır. Hedef, karamel tatlılığı ile temiz asiditenin aynı anda görünmesi.'
        };
        if (text.includes('antigua') || text.includes('guatemala')) return {
          family:'sweet',
          lead:'Tatlı gövdeyi, kakao/karamel tarafını ve dengeli asiditeyi birlikte koruyacak şekilde hazırlanan başlangıç reçetesi.',
          tune:'Tatlılık düşükse biraz daha ince öğüt; bitiş kuruyorsa bir kademe kalınlaştır. Çok yüksek sıcaklıkla gövdeyi sertleştirmemeye çalış.'
        };
        if (text.includes('honduras')) return {
          family:'soft',
          lead:'Yumuşak içimi ve günlük dengeyi koruyup fincanı düzleştirmeden yeterli tatlılık çıkaracak güvenli başlangıç reçetesi.',
          tune:'Fincan fazla sakin kalırsa öğütümü küçük bir adım incelt. Acılık yükselirse önce teması kısalt; dozu değiştirmeden dengeyi koru.'
        };
        if (text.includes('cerrado') || text.includes('rio minas') || text.includes('sumatra') || text.includes('brazil')) return {
          family:'body',
          lead:'Düşük asidite, çikolata-karamel karakteri ve güçlü gövdeyi temiz bir bitişle gösterecek şekilde ayarlanmış başlangıç reçetesi.',
          tune:'Ağır veya kuru hissedilirse öğütümü kalınlaştır. Gövde yerinde fakat tatlılık düşükse teması küçük bir miktar uzat; sıcaklığı gereksiz yükseltme.'
        };
        return {
          family:'neutral',
          lead:'Bu kahvenin tatlılık, gövde ve aromalarını dengeli biçimde gösterecek güvenli bir başlangıç reçetesi.',
          tune:'İlk fincandan sonra yalnızca tek değişkeni değiştir: önce öğütüm, sonra sıcaklık. Böylece hangi ayarın fincanı iyileştirdiğini net takip edebilirsin.'
        };
      }

      function tunedMetrics(method, profile) {
        const metrics = method.metrics.map(metric => [...metric]);
        const temp = metrics.find(metric => metric[0] === 'Sıcaklık');
        if (!temp) return metrics;
        if (profile.family === 'el-jardin') {
          if (selectedMethodKey === 'v60') temp[1] = '91°C';
          else if (selectedMethodKey === 'chemex') temp[1] = '92°C';
          else if (selectedMethodKey === 'aeropress') temp[1] = '89°C';
          else if (selectedMethodKey === 'japanese') temp[1] = '91°C';
        }
        if (profile.family === 'anaerobic' && !['cold-brew'].includes(selectedMethodKey)) temp[1] = selectedMethodKey === 'espresso' ? '92°C' : '91°C';
        if (profile.family === 'floral' && ['v60','chemex','japanese'].includes(selectedMethodKey)) temp[1] = '92°C';
        if (profile.family === 'body' && ['v60','chemex','filter-machine','french-press'].includes(selectedMethodKey)) temp[1] = selectedMethodKey === 'french-press' ? '94°C' : '93°C';
        return metrics;
      }

      function preloadRecipeImage(product) {
        const src = product?.featured_image_recipe || product?.featured_image || product?.featured_image_card || '';
        if (!src) return Promise.resolve('');
        if (recipeImageCache.has(src)) return recipeImageCache.get(src);
        const task = new Promise(resolve => {
          const img = new Image();
          img.decoding = 'async';
          img.fetchPriority = 'high';
          img.onload = () => {
            const done = () => resolve(src);
            if (typeof img.decode === 'function') img.decode().then(done).catch(done);
            else done();
          };
          img.onerror = () => resolve('');
          img.src = src;
          if (img.complete && img.naturalWidth) {
            const done = () => resolve(src);
            if (typeof img.decode === 'function') img.decode().then(done).catch(done);
            else done();
          }
        });
        recipeImageCache.set(src, task);
        return task;
      }

      async function renderProducts(method) {
        if (!productsBox) return;
        productsBox.classList.add('is-changing');
        const key = selectedMethodKey;
        await ensureRecipes().catch(() => {});
        if (key !== selectedMethodKey) return;
        const selected = selectProducts(method);
        if (recommendTitle) recommendTitle.textContent = selected.length === 1 ? 'En uygun kahve' : 'En uygun 2 kahve';
        selected.forEach(preloadRecipeImage);
        setTimeout(() => {
          productsBox.classList.remove('is-empty');
          if (!selected.length) {
            productsBox.innerHTML = '<div class="wms-brew-empty">Bu yöntem için uygun kahve henüz seçkiye eklenmemiş.</div>';
          } else {
            productsBox.innerHTML = selected.map((product, index) => {
              const cardImage = product.featured_image_card || product.featured_image || product.featured_image_recipe || '';
              return `
              <article class="wms-brew-product" data-wms-brew-product-card>
                <div class="wms-brew-product__image">
                  ${cardImage ? `<a class="wms-brew-product__image-link" href="${escapeHTML(product.url)}" aria-label="${escapeHTML(product.title)} ürününe git"><img src="${escapeHTML(cardImage)}" alt="${escapeHTML(product.title)}" loading="eager" decoding="async" fetchpriority="high"></a>` : ''}
                  <span>${index === 0 ? 'En uygun' : 'Alternatif'}</span>
                </div>
                <div class="wms-brew-product__body">
                  <div class="wms-brew-product__meta">
                    <b>${escapeHTML(product.title)}</b>
                    <small>${escapeHTML(money(product.price))}</small>
                  </div>
                  <div class="wms-brew-product__actions">
                    <button type="button" data-wms-brew-product-recipe data-handle="${escapeHTML(product.handle)}">Reçeteyi gör</button>
                    <a href="${escapeHTML(product.url)}">Ürüne git</a>
                  </div>
                </div>
              </article>`;
            }).join('');
          }
          productsBox.classList.remove('is-changing');
        }, 110);
      }

      function activateMethod(key) {
        const method = methods[key];
        if (!method) return;
        selectedMethodKey = key;
        methodButtons.forEach(button => {
          const active = button.dataset.wmsBrewMethod === key;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        if (recommendKicker) recommendKicker.textContent = `${method.label} için`;
        if (recommendTitle) recommendTitle.textContent = 'En uygun 2 kahve';
        renderProducts(method);
      }

      let recipeTransitioning = false;

      function setRecipeTypeScale(scale = 1) {
        if (!recipeView) return;
        const mobile = innerWidth < 750;
        const width = Math.max(420, recipeView.clientWidth || 560);
        const height = Math.max(430, recipeView.clientHeight || 600);
        const widthFactor = Math.min(1.08, Math.max(.90, width / 610));
        const heightFactor = Math.min(1.05, Math.max(.86, height / 610));
        const factor = scale * Math.min(widthFactor, heightFactor);

        // Product identity stays deliberately small; the rest of the recipe
        // may grow to use the complete area above the action divider.
        const productFactor = Math.min(1, Math.max(.88, width / 610));
        recipeView.style.setProperty('--wms-recipe-title-size', `${Math.max(mobile ? 22 : 25, Math.min(mobile ? 49 : 47, 37 * factor)).toFixed(2)}px`);
        recipeView.style.setProperty('--wms-recipe-product-size', `${Math.max(7.5, Math.min(9, 8.4 * productFactor)).toFixed(2)}px`);
        recipeView.style.setProperty('--wms-recipe-lead-size', `${Math.max(8.8, Math.min(mobile ? 13 : 12, 10 * factor)).toFixed(2)}px`);
        recipeView.style.setProperty('--wms-recipe-step-size', `${Math.max(7.8, Math.min(mobile ? 11.5 : 10.5, 8.9 * factor)).toFixed(2)}px`);
        recipeView.style.setProperty('--wms-recipe-fine-size', `${Math.max(7.5, Math.min(mobile ? 10.5 : 9.8, 8.5 * factor)).toFixed(2)}px`);
        recipeView.style.setProperty('--wms-recipe-metric-label-size', `${Math.max(6, Math.min(7.2, 6.5 * factor)).toFixed(2)}px`);
        recipeView.style.setProperty('--wms-recipe-metric-value-size', `${Math.max(8.2, Math.min(10.5, 9.2 * factor)).toFixed(2)}px`);
        recipeView.style.setProperty('--wms-recipe-label-size', `${Math.max(6.1, Math.min(7.8, 6.8 * factor)).toFixed(2)}px`);
      }

      function getRecipeUsedHeight() {
        if (!recipeMain) return 0;
        const mainRect = recipeMain.getBoundingClientRect();
        let bottom = mainRect.top;
        [...recipeMain.children].forEach(child => {
          if (child.hidden || getComputedStyle(child).display === 'none') return;
          const rect = child.getBoundingClientRect();
          bottom = Math.max(bottom, rect.bottom);
        });
        return Math.max(0, bottom - mainRect.top);
      }

      function fitRecipeTypography() {
        if (!recipeView || !recipeMain) return;
        const mobile = innerWidth < 750;

        // Measure against the divider itself rather than an arbitrary inset.
        // The action row owns the divider, so this is the exact vertical area
        // the recipe copy is allowed to use after the V22 5px lift.
        const mainRect = recipeMain.getBoundingClientRect();
        const actionsRect = recipeActions?.getBoundingClientRect();
        const available = Math.max(
          0,
          actionsRect ? actionsRect.top - mainRect.top : recipeMain.clientHeight
        );

        // Search for the largest scale that fills the complete copy area without
        // crossing the divider. Content length is naturally accounted for by the
        // measured rendered height, so short recipes can grow and long ones shrink.
        let low = mobile ? .54 : .68;
        let high = mobile ? 1.86 : 1.34;
        let best = low;

        for (let i = 0; i < 13; i += 1) {
          const mid = (low + high) / 2;
          setRecipeTypeScale(mid);
          void recipeMain.offsetHeight;
          const used = getRecipeUsedHeight();
          if (used <= available) {
            best = mid;
            low = mid;
          } else {
            high = mid;
          }
        }
        setRecipeTypeScale(best);
      }

      function createProductImageFlight(sourceImage) {
        if (!sourceImage || matchMedia('(prefers-reduced-motion: reduce)').matches) {
          return { play: () => Promise.resolve(), remove: () => {} };
        }
        const from = sourceImage.getBoundingClientRect();
        const sourceSrc = sourceImage.currentSrc || sourceImage.src || '';
        if (!from.width || !from.height || !sourceSrc) {
          return { play: () => Promise.resolve(), remove: () => {} };
        }

        const flying = document.createElement('img');
        flying.className = 'wms-brew-flight';
        flying.src = sourceSrc;
        flying.alt = '';
        Object.assign(flying.style, {
          left: `${from.left}px`,
          top: `${from.top}px`,
          width: `${from.width}px`,
          height: `${from.height}px`,
          objectPosition: getComputedStyle(sourceImage).objectPosition || '50% 50%',
          opacity: '1'
        });
        document.body.appendChild(flying);

        return {
          play: targetImage => {
            if (!targetImage) { flying.remove(); return Promise.resolve(); }
            const to = targetImage.getBoundingClientRect();
            if (!to.width || !to.height) { flying.remove(); return Promise.resolve(); }
            const animation = flying.animate([
              {
                left: `${from.left}px`, top: `${from.top}px`, width: `${from.width}px`, height: `${from.height}px`,
                opacity: 1, boxShadow: '0 12px 34px rgba(8,16,11,.14)'
              },
              {
                offset: .78,
                left: `${to.left}px`, top: `${to.top}px`, width: `${to.width}px`, height: `${to.height}px`,
                opacity: 1, boxShadow: '0 2px 10px rgba(8,16,11,.04)'
              },
              {
                left: `${to.left}px`, top: `${to.top}px`, width: `${to.width}px`, height: `${to.height}px`,
                opacity: 0, boxShadow: '0 0 0 rgba(8,16,11,0)'
              }
            ], {
              duration: 860,
              easing: 'cubic-bezier(.22,.74,.18,1)',
              fill: 'forwards'
            });
            return animation.finished.catch(() => {}).then(() => flying.remove());
          },
          remove: () => flying.remove()
        };
      }

      function setInitialRecipeScale() {
        const contentLength = [
          recipeProduct?.textContent,
          recipeMethod?.textContent,
          recipeLead?.textContent,
          recipeSteps?.textContent,
          fineTune?.textContent
        ].join(' ').length;
        const scale = contentLength > 940 ? .86 : contentLength > 800 ? .91 : contentLength > 660 ? .96 : 1;
        setRecipeTypeScale(scale);
      }

      function waitForBrewView(node, fallback = 680) {
        if (!node || matchMedia('(prefers-reduced-motion: reduce)').matches) return Promise.resolve();
        return new Promise(resolve => {
          let settled = false;
          const finish = () => {
            if (settled) return;
            settled = true;
            node.removeEventListener('transitionend', onEnd);
            clearTimeout(timer);
            resolve();
          };
          const onEnd = event => {
            if (event.target === node && (event.propertyName === 'opacity' || event.propertyName === 'transform')) finish();
          };
          const timer = setTimeout(finish, fallback);
          node.addEventListener('transitionend', onEnd);
        });
      }

      async function openRecipe(product, trigger) {
        const method = methods[selectedMethodKey];
        if (!method || !product || recipeTransitioning) return;
        recipeTransitioning = true;
        lastRecipeButton = trigger || null;

        // Keep the shared product-specific recipe catalog out of first paint.
        // The built-in method fallback below keeps this view usable even if the
        // optional asset is unavailable.
        await ensureRecipes().catch(() => {});

        const sourceImage = trigger?.closest?.('[data-wms-brew-product-card]')?.querySelector?.('.wms-brew-product__image img');
        const sourceSrc = sourceImage?.currentSrc || sourceImage?.src || product.featured_image_card || product.featured_image || '';
        const highResPromise = preloadRecipeImage(product);
        const profile = productProfile(product);
        const preparedRecipe = window.WemondRecipeUtils?.find?.(product.handle, selectedMethodKey) || null;
        const preparedProduct = window.WemondProductRecipes?.[product.handle] || (product.handle === 'honduras-shb' ? window.WemondProductRecipes?.['honduras-shg'] : null);
        const grindDescriptor = preparedRecipe ? (window.WemondRecipeUtils?.grinderLabel?.(preparedRecipe.key) || 'Orta') : '';
        const grindRefs = window.WemondRecipeUtils?.grinderRefs?.(preparedRecipe?.grind, preparedRecipe?.key || selectedMethodKey) || '';
        const metrics = preparedRecipe
          ? [
              ['Oran', preparedRecipe.ratio],
              ['Öğütüm', [grindDescriptor, grindRefs].filter(Boolean).join(' · ')],
              ['Su sıcaklığı', preparedRecipe.temperature],
              ['Süre', preparedRecipe.duration]
            ]
          : tunedMetrics(method, profile).map(metric => metric[0] === 'Öğütüm' && grindRefs
            ? [metric[0], [metric[1], grindRefs].filter(Boolean).join(' · ')]
            : metric);

        if (recipeKicker) recipeKicker.textContent = 'Wemond demleme rehberi';
        if (recipeProduct) recipeProduct.textContent = product.title;
        if (recipeMethod) recipeMethod.textContent = `${preparedRecipe?.label || method.label} reçetesi.`;
        if (recipeLead) recipeLead.textContent = preparedProduct?.profile || profile.lead;
        if (recipeMetrics) recipeMetrics.innerHTML = metrics.map(metric => `<span><small>${escapeHTML(metric[0])}</small><b>${escapeHTML(metric[1])}</b></span>`).join('');
        if (recipeSteps) recipeSteps.innerHTML = preparedRecipe?.steps_html || method.steps.map(step => `<li>${escapeHTML(step)}</li>`).join('');
        if (fineTune) fineTune.textContent = preparedRecipe?.note || profile.tune;
        if (detailProductLink) detailProductLink.href = product.url;

        if (productImage) {
          productImage.src = sourceSrc || product.featured_image_recipe || product.featured_image || '';
          productImage.alt = product.title || '';
        }
        if (mediaMethod) mediaMethod.textContent = method.label;
        if (mediaProduct) mediaProduct.textContent = product.title;
        if (recipeView) recipeView.scrollTop = 0;

        // Entry now mirrors the existing Back transition: both old and new
        // views stay mounted and crossfade/slide together. No image flight.
        section.classList.remove('is-recipe-entered','is-recipe-leaving','is-recipe-leave-active','is-recipe-enter-active');
        section.classList.add('is-recipe','is-recipe-entering');
        if (selectionView) selectionView.hidden = false;
        if (recipeView) recipeView.hidden = false;
        if (defaultMedia) defaultMedia.hidden = false;
        if (productMedia) productMedia.hidden = false;
        if (backButton) backButton.hidden = false;

        setInitialRecipeScale();
        fitRecipeTypography();

        await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        section.classList.add('is-recipe-enter-active');

        // Upgrade to the preloaded high-resolution image during the same soft
        // transition, without delaying the first frame of the recipe view.
        highResPromise.then(src => {
          if (!src || !productImage || !section.classList.contains('is-recipe')) return;
          if (productImage.src !== src) productImage.src = src;
        });

        await waitForBrewView(recipeView);
        if (selectionView) selectionView.hidden = true;
        if (defaultMedia) defaultMedia.hidden = true;
        section.classList.remove('is-recipe-entering','is-recipe-enter-active');
        section.classList.add('is-recipe-entered');
        fitRecipeTypography();
        recipeTransitioning = false;
      }

      async function closeRecipe() {
        if (recipeTransitioning) return;
        recipeTransitioning = true;

        if (selectionView) selectionView.hidden = false;
        if (defaultMedia) defaultMedia.hidden = false;
        section.classList.add('is-recipe-leaving');

        await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        section.classList.add('is-recipe-leave-active');
        await waitForBrewView(recipeView);
        section.classList.remove('is-recipe','is-recipe-entering','is-recipe-entered','is-recipe-leaving','is-recipe-leave-active');
        if (recipeView) recipeView.hidden = true;
        if (productMedia) productMedia.hidden = true;
        if (backButton) backButton.hidden = true;
        if (productImage) productImage.style.opacity = '';
        recipeTransitioning = false;
        if (lastRecipeButton?.isConnected) requestAnimationFrame(() => lastRecipeButton.focus({preventScroll:true}));
      }

      if ('ResizeObserver' in window && recipeView) {
        const recipeResizeObserver = new ResizeObserver(() => {
          if (!section.classList.contains('is-recipe') || recipeTransitioning) return;
          fitRecipeTypography();
        });
        recipeResizeObserver.observe(recipeView);
      }

      methodButtons.forEach(button => button.addEventListener('click', () => {
        ensureRecipes().catch(() => {});
        activateMethod(button.dataset.wmsBrewMethod);
      }));
      backButton?.addEventListener('click', closeRecipe);

      section.addEventListener('click', async event => {
        const recipeButton = event.target.closest?.('[data-wms-brew-product-recipe]');
        if (!recipeButton) return;
        const product = catalog.find(item => item.handle === recipeButton.dataset.handle);
        if (product) {
          await ensureRecipes().catch(() => {});
          openRecipe(product, recipeButton);
        }
      });
    });
  }

  let sharedMarqueePixelsPerSecond = 0;

  function initMeasuredMarquee(rail, { trackSelector, sourceSelector, cloneSelector, speedVariable, minExtra = 120, speedRole = 'independent' }) {
    const track = rail.querySelector(trackSelector);
    const source = rail.querySelector(sourceSelector);
    const clone = rail.querySelector(cloneSelector);
    if (!track || !source || !clone) return;

    const unitHTML = source.innerHTML;
    let animation = null;
    let resizeRaf = 0;
    let visible = false;
    let measured = false;

    const measure = () => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        animation?.cancel?.();
        source.innerHTML = unitHTML;
        const railWidth = rail.clientWidth;
        const unitWidth = Math.max(1, source.getBoundingClientRect().width);
        const repeatCount = Math.min(13, Math.max(1, Math.ceil((railWidth + minExtra) / unitWidth)));
        if (repeatCount > 1) {
          source.insertAdjacentHTML('beforeend', unitHTML.repeat(repeatCount - 1));
        }
        clone.innerHTML = source.innerHTML;

        const distance = Math.max(1, Math.round(source.getBoundingClientRect().width));
        const seconds = parseFloat(getComputedStyle(rail).getPropertyValue(speedVariable)) || 60;
        let duration = Math.max(1000, seconds * 1000);
        if (speedRole === 'master') {
          // data-wms-speed-scale slows only this rail (0.5 = half speed); the
          // shared pace that follower rails use stays the original one.
          const scale = parseFloat(rail.dataset.wmsSpeedScale) || 1;
          duration = Math.max(1000, (seconds * 1000) / scale);
          sharedMarqueePixelsPerSecond = distance / Math.max(1, seconds);
          document.dispatchEvent(new CustomEvent('wemond:marquee-speed', { detail: { pixelsPerSecond: sharedMarqueePixelsPerSecond } }));
        } else if (speedRole === 'follower' && sharedMarqueePixelsPerSecond > 0) {
          duration = Math.max(1000, (distance / sharedMarqueePixelsPerSecond) * 1000);
        }
        track.style.transform = 'translate3d(0,0,0)';

        if (!reduced.matches && distance > 1 && track.animate) {
          animation = track.animate(
            [
              { transform: 'translate3d(0px,0,0)' },
              { transform: `translate3d(${-distance}px,0,0)` }
            ],
            { duration, iterations: Infinity, easing: 'linear' }
          );
          if (!visible || document.hidden) animation.pause();
        }
      });
    };

    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible && !measured) {
        measured = true;
        measure();
        return;
      }
      if (!animation) return;
      if (visible && !document.hidden && !reduced.matches) animation.play();
      else animation.pause();
    }, { threshold: 0 });
    observer.observe(rail);

    const onVisibility = () => {
      if (!animation) return;
      if (visible && !document.hidden && !reduced.matches) animation.play();
      else animation.pause();
    };
    document.addEventListener('visibilitychange', onVisibility);

    document.fonts?.ready?.then(() => { if (measured) measure(); }).catch(() => {});
    source.querySelectorAll('img').forEach(img => {
      if (!img.complete) img.addEventListener('load', () => { if (measured) measure(); }, { once: true });
    });
    const ro = 'ResizeObserver' in window ? new ResizeObserver(() => { if (measured) measure(); }) : null;
    ro?.observe(rail);
    reduced.addEventListener?.('change', () => { if (measured) measure(); });
    if (speedRole === 'follower') {
      document.addEventListener('wemond:marquee-speed', event => {
        const nextSpeed = Number(event.detail?.pixelsPerSecond) || 0;
        if (nextSpeed > 0) sharedMarqueePixelsPerSecond = nextSpeed;
        if (measured) measure();
      });
    }
  }

  function initLogoRail(root = document) {
    root.querySelectorAll?.('[data-wms-logo-rail]:not([data-wms-logo-ready])').forEach(rail => {
      rail.dataset.wmsLogoReady = 'true';
      initMeasuredMarquee(rail, {
        trackSelector: '[data-wms-logo-track]',
        sourceSelector: '[data-wms-logo-source]',
        cloneSelector: '[data-wms-logo-clone]',
        speedVariable: '--wms-logo-speed',
        minExtra: 160,
        speedRole: 'follower'
      });
    });
  }

  function initSharpTicker(root = document) {
    root.querySelectorAll?.('[data-wms-ticker]:not([data-wms-sharp-ready])').forEach(ticker => {
      ticker.dataset.wmsSharpReady = 'true';
      initMeasuredMarquee(ticker, {
        trackSelector: '[data-wms-ticker-track]',
        sourceSelector: '[data-wms-ticker-source]',
        cloneSelector: '[data-wms-ticker-clone]',
        speedVariable: '--wms-ticker-speed',
        minExtra: 100,
        speedRole: 'master'
      });
    });
  }

  function init(root = document) {
    setChromeHeight();
    document.documentElement.classList.add('wms-motion');
    initStory(root); initReveal(root); initTaste(root); initDynamicMega(root); initBrewDialogs(root); initSharpTicker(root); initLogoRail(root);
  }
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', event => init(event.target));
  addEventListener('resize', setChromeHeight, {passive:true});
  addEventListener('pageshow', () => {
    setChromeHeight();
    document.querySelectorAll('[data-wms-story]').forEach(story => story._wmsRender?.());
  });
})();
